import { EXTERNAL_LINKS, TypeformEmbed } from '@psyomics/ui-common';
import { Button, LoadingSpinner, PageLayout, Puck, useDynamicCrumb, Woman4 } from '@psyomics/ui-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IllustrationWithArc } from '../../../shared/components/header-illustration-layout/header-illustration-layout';
import { ServiceUserDetail } from '../../account/generated/service-user';
import { useGetServiceUserQuery } from '../../account/hooks/use-get-service-user-query';
import { ContentType, OrganisationContent, ReferralInvite } from '../../referral/generated/referral';
import { useReferralInviteContentQuery } from '../../referral/hooks/use-referral-invite-query';

const TYPEFORM_END_QUESTIONNAIRE_PATIENT_FEEDBACK = 'MsXUWjnX';

const getCrumb = (serviceUser?: ServiceUserDetail, invite?: ReferralInvite) =>
  serviceUser?.hasTrustReferral ? invite?.referralSession.referral.service?.description : undefined;

const getEndingPageMetadata = (invite: ReferralInvite) => {
  return {
    givenName: invite.givenName ?? '',
    orgName: invite.referralSession.referral.organisation?.name ?? '',
    psyomicsPrivacyPolicyUrl: EXTERNAL_LINKS.PRIVACY_POLICY,
  };
};

const getContentOrThrow = (orgContent: OrganisationContent, inviteId: string) => {
  try {
    return JSON.parse(orgContent.content.definition);
  } catch {
    throw new Error('ending page content definition is not valid JSON for invite: ' + inviteId);
  }
};

const EndScreen: React.FC<{ readonly invite?: ReferralInvite }> = ({ invite }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [organisationContent, fetchingOrgContent, error] = useReferralInviteContentQuery(
    ContentType.EndingPage,
    invite?.publicId
  );
  const [serviceUser, fetchingServiceUser] = useGetServiceUserQuery();
  useDynamicCrumb(getCrumb(serviceUser, invite));

  if (fetchingOrgContent || fetchingServiceUser) return <LoadingSpinner />;
  if (error) throw error;
  if (!invite) throw new Error('invite not found');
  if (!organisationContent) throw new Error('ending page content not defined for invite: ' + invite.id);

  const organisationCode = invite.referralSession.referral.organisation?.code;

  return (
    <PageLayout>
      <IllustrationWithArc>
        <Woman4 className="w-full h-auto max-h-[238px]" />
      </IllustrationWithArc>
      <div className="pt-6" data-testid="referral-end-screen">
        <div>
          <Puck.ContentPage.Render
            metadata={getEndingPageMetadata(invite)}
            reportDefinition={getContentOrThrow(organisationContent, invite.id)}
          />
          {serviceUser?.hasTrustReferral && (
            <Button appearance="secondary" className="w-full" onClick={() => navigate('/')}>
              {t('common.buttons.home')}
            </Button>
          )}
        </div>
      </div>
      <TypeformEmbed contract={organisationCode} type={TYPEFORM_END_QUESTIONNAIRE_PATIENT_FEEDBACK} />
    </PageLayout>
  );
};

export { EndScreen };
